import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Clock, Copy, Eye, User } from "lucide-react";
import type { ActionStatus, ResourceActionListItem } from "@/types/admin";
import { cn, formatDateTime } from "@/lib/utils";
import {
  RESOURCE_ACTION_COLUMNS,
  useResourceActionColumnsStore,
  type ResourceActionColumnId,
} from "@/stores/resourceActionColumnsStore";

interface ResourceActionTableProps {
  actions: ResourceActionListItem[];
  /** Index of the first row on this page, so the ordinal column continues across pages. */
  startIndex: number;
  onViewDetails: (action: ResourceActionListItem) => void;
  /**
   * Draw every column and ignore the column-visibility store. For a screen with no column menu —
   * the resource-detail tab — where a column hidden by default or by a stored preference would be
   * gone with no way to bring it back.
   */
  showAllColumns?: boolean;
  /**
   * Columns this screen never draws, whatever the store or `showAllColumns` says. The
   * resource-detail tab leaves out `tenant` and `user`: every row there belongs to the one machine
   * on screen, and both stay in the row's detail dialog.
   */
  omitColumns?: readonly ResourceActionColumnId[];
}

/** Rendered in place of a value the row does not carry. */
const EMPTY = "—";

/**
 * How wide each column is drawn. Kept here rather than in the store because a width is
 * presentation, not preference — the store holds only what gets written to localStorage. Typing
 * it as a full `Record` over the id union is the point: adding a column to
 * `RESOURCE_ACTION_COLUMNS` without giving it a width is a compile error.
 *
 * Sized to what each cell actually carries, not to the viewport: resource and tenant fit a full
 * 36-character GUID under the name, user a full e-mail, created a "dd-MM-yyyy HH:mm" stamp, and
 * type the longest ActionType ("Provision", "Terminate"), and description a sentence like
 * "VM provisioning initiated, polling for completion". The sum (~2200px) is wider than the content
 * area on a normal screen, so the table scrolls horizontally instead of truncating.
 */
const COLUMN_WIDTHS: Record<ResourceActionColumnId, string> = {
  index: "w-14",
  createdAt: "w-40",
  resource: "w-80",
  type: "w-32",
  status: "w-32",
  description: "w-80",
  tenant: "w-72",
  user: "w-64",
  duration: "w-24",
  error: "w-96",
  actions: "w-16",
};

type StatusBadgeConfig = { label: string; chip: string };

const NEUTRAL_CHIP = "border-hairline-strong bg-panel-2 text-ink-3";

/**
 * Colour per `ActionStatus`. Same three-role palette as `PaymentStatusBadge` (`chip` is the tint
 * plus border plus text, never a saturated fill) — Completed reads as done, Failed and Cancelled
 * are terminal but only one of them is a problem, and InProgress/Pending are the two an operator
 * might still be waiting on.
 */
const ACTION_STATUS_BADGE: Record<ActionStatus, StatusBadgeConfig> = {
  Pending: {
    label: "Pending",
    chip: "border-info/30 bg-info-soft text-info-ink",
  },
  InProgress: {
    label: "In Progress",
    chip: "border-warn/30 bg-warn-soft text-warn-ink",
  },
  Completed: {
    label: "Completed",
    chip: "border-ok/30 bg-ok-soft text-ok-ink",
  },
  Failed: {
    label: "Failed",
    chip: "border-destructive/30 bg-destructive-soft text-destructive-ink",
  },
  Cancelled: { label: "Cancelled", chip: NEUTRAL_CHIP },
};

function ActionStatusBadge({ status }: { status: ActionStatus }) {
  const config = ACTION_STATUS_BADGE[status] ?? {
    label: status,
    chip: NEUTRAL_CHIP,
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium shadow-none",
        config.chip,
      )}
    >
      {config.label}
    </Badge>
  );
}

/**
 * The VM's provider UUID, with a copy button that never triggers the row's own click targets.
 * `stopPropagation` matters here: the resource-name button sits in the same cell.
 */
function CopyableUuid({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context; the text stays selectable.
    }
  };

  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span
        className="truncate font-mono text-[11px] text-muted-foreground select-all"
        title={value}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied" : "Copy VM UUID"}
        aria-label="Copy VM UUID"
        className="shrink-0 cursor-pointer rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus:outline-none"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

/** Renders a millisecond duration the way an operator reads it: whole seconds past one second. */
function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return EMPTY;
  if (durationMs < 1000) return `${durationMs} ms`;
  return `${(durationMs / 1000).toFixed(1)} s`;
}

export function ResourceActionTable({
  actions,
  startIndex,
  onViewDetails,
  showAllColumns = false,
  omitColumns,
}: ResourceActionTableProps) {
  const navigate = useNavigate();

  // Subscribing to `hidden` (the value that changes) and deriving the predicate locally, not
  // subscribing to the store's own `isVisible` — that one is a stable function reading the latest
  // state, so a component subscribed to it re-renders for nothing and never re-renders on toggle.
  const hiddenColumns = useResourceActionColumnsStore((state) => state.hidden);
  const isVisible = (id: ResourceActionColumnId) => {
    if (omitColumns?.includes(id)) {
      return false;
    }

    return showAllColumns || !hiddenColumns.includes(id);
  };

  return (
    <>
      {/* Mobile: one card per row, same reasoning as AuditLogTable — the desktop column set does
          not survive a phone width. */}
      <div className="min-h-0 flex-1 overflow-auto rounded-md border md:hidden">
        <Table>
          <TableBody>
            {actions.map((action) => (
              <TableRow key={action.id}>
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{action.type}</Badge>
                      <ActionStatusBadge status={action.status} />
                    </div>
                    <div>
                      {action.isResourceDeleted ? (
                        <span className="text-sm font-medium text-muted-foreground">
                          {action.resourceName}{" "}
                          <Badge
                            variant="outline"
                            className="border-destructive/30 bg-destructive-soft text-[10px] text-destructive-ink"
                          >
                            Deleted
                          </Badge>
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            navigate(`/admin/resources/${action.resourceId}`)
                          }
                          className="cursor-pointer text-left text-sm font-medium text-primary hover:underline"
                        >
                          {action.resourceName}
                        </button>
                      )}
                      {action.vmUuid && (
                        <div className="mt-1">
                          <CopyableUuid value={action.vmUuid} />
                        </div>
                      )}
                    </div>
                    {action.description && (
                      <p className="text-sm text-muted-foreground">
                        {action.description}
                      </p>
                    )}
                    {action.errorMessage && (
                      <p
                        className="text-sm text-destructive-ink"
                        title={action.errorMessage}
                      >
                        {action.errorMessage}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {action.userEmail || "System"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(action.createdAt)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs"
                        onClick={() => onViewDetails(action)}
                      >
                        <Eye className="h-3 w-3" />
                        Details
                      </Button>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Desktop: same scroll-container fix as PaymentListTable / ResourceMonitoringTable — the
          horizontal scrollbar sits on THIS box, not on `components/ui/table.tsx`'s own wrapper,
          which is as tall as the whole table and would otherwise render its scrollbar off the
          bottom of the viewport with the sticky header never resolving against it. */}
      <div className="scrollbar-thin hidden min-h-0 flex-1 overflow-auto rounded-md border md:block [&>div]:overflow-visible">
        <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
          {/* Generated from RESOURCE_ACTION_COLUMNS rather than written out, so a hidden column
              cannot leave its <col> behind — which would shift every width after it by one and is
              the one drift the browser gives no error for. */}
          <colgroup>
            {RESOURCE_ACTION_COLUMNS.filter((column) =>
              isVisible(column.id),
            ).map((column) => (
              <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
            ))}
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              {isVisible("index") && (
                <TableHead className="text-center">No.</TableHead>
              )}
              {isVisible("createdAt") && <TableHead>Created</TableHead>}
              {isVisible("resource") && <TableHead>Resource</TableHead>}
              {isVisible("type") && <TableHead>Action</TableHead>}
              {isVisible("status") && <TableHead>Status</TableHead>}
              {isVisible("description") && <TableHead>Description</TableHead>}
              {isVisible("tenant") && <TableHead>Tenant</TableHead>}
              {isVisible("user") && <TableHead>User</TableHead>}
              {isVisible("duration") && (
                <TableHead className="text-right">Duration</TableHead>
              )}
              {isVisible("error") && <TableHead>Error</TableHead>}
              {isVisible("actions") && (
                <TableHead className="text-center">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {actions.map((action, index) => (
              <TableRow key={action.id}>
                {isVisible("index") && (
                  <TableCell className="text-center text-xs font-medium text-muted-foreground">
                    {startIndex + index + 1}
                  </TableCell>
                )}
                {isVisible("createdAt") && (
                  <TableCell>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDateTime(action.createdAt)}
                    </span>
                  </TableCell>
                )}
                {isVisible("resource") && (
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        {action.isResourceDeleted ? (
                          <span
                            className="truncate text-sm font-medium text-muted-foreground"
                            title={action.resourceName}
                          >
                            {action.resourceName}
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              navigate(`/admin/resources/${action.resourceId}`)
                            }
                            className="cursor-pointer truncate text-left text-sm font-medium text-primary hover:underline"
                            title={action.resourceName}
                          >
                            {action.resourceName}
                          </button>
                        )}
                        {action.isResourceDeleted && (
                          <Badge
                            variant="outline"
                            className="shrink-0 border-destructive/30 bg-destructive-soft px-1.5 text-[10px] text-destructive-ink"
                          >
                            Deleted
                          </Badge>
                        )}
                      </div>
                      {action.vmUuid && <CopyableUuid value={action.vmUuid} />}
                    </div>
                  </TableCell>
                )}
                {isVisible("type") && (
                  <TableCell>
                    <Badge variant="outline">{action.type}</Badge>
                  </TableCell>
                )}
                {isVisible("status") && (
                  <TableCell>
                    <ActionStatusBadge status={action.status} />
                  </TableCell>
                )}
                {isVisible("description") && (
                  <TableCell>
                    {action.description ? (
                      <p
                        className="truncate text-sm"
                        title={action.description}
                      >
                        {action.description}
                      </p>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {EMPTY}
                      </span>
                    )}
                  </TableCell>
                )}
                {isVisible("tenant") && (
                  <TableCell>
                    <div className="truncate font-medium">
                      {action.tenantName}
                    </div>
                    <div
                      className="truncate font-mono text-[11px] text-muted-foreground"
                      title={action.tenantId}
                    >
                      {action.tenantId}
                    </div>
                  </TableCell>
                )}
                {isVisible("user") && (
                  <TableCell>
                    <span className="truncate text-sm" title={action.userEmail}>
                      {action.userEmail || "System"}
                    </span>
                  </TableCell>
                )}
                {isVisible("duration") && (
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {formatDuration(action.durationMs)}
                  </TableCell>
                )}
                {isVisible("error") && (
                  <TableCell>
                    {action.errorMessage ? (
                      <p
                        className="truncate text-sm text-destructive-ink"
                        title={action.errorMessage}
                      >
                        {action.errorMessage}
                      </p>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {EMPTY}
                      </span>
                    )}
                  </TableCell>
                )}
                {isVisible("actions") && (
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View action details"
                      onClick={() => onViewDetails(action)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
